import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Mail, Lock, ArrowRight, Loader2, AlertCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

const loginBaseSchema = z.object({
    email: z.string().email(),
    password: z.string(),
});

type LoginSchema = z.infer<typeof loginBaseSchema>;

interface AuthLoginProps {
    loading: boolean;
    onLogin: (data: LoginSchema) => Promise<void> | void;
    onForgotPassword: () => void;
}

const AuthLogin: React.FC<AuthLoginProps> = ({
    loading,
    onLogin,
    onForgotPassword,
}) => {
    const { t } = useTranslation();

    const loginSchema = z.object({
        email: z.string().email(t('auth.validation.email_invalid')),
        password: z.string().min(1, t('auth.validation.password_required')),
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginSchema>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            email: "",
            password: "",
        },
    });

    const onSubmit = (data: LoginSchema) => {
        onLogin(data);
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 md:space-y-5">

            <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
                    {t('login.email')}
                </label>
                <div className="relative group">
                    <Mail
                        className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${errors.email ? "text-red-400" : "text-gray-400 group-focus-within:text-clip-500"
                            }`}
                        size={18}
                    />
                    <input
                        type="email"
                        {...register("email")}
                        className={`w-full pl-11 pr-4 py-3.5 bg-gray-50 border rounded-xl outline-none transition-all font-medium text-gray-900 placeholder:text-gray-400
                            ${errors.email
                                ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                                : "border-gray-200 focus:border-clip-500 focus:ring-2 focus:ring-clip-200"
                            }
                        `}
                        placeholder={t('auth.placeholders.email')}
                    />
                </div>
                {errors.email && (
                    <p className="text-red-500 text-xs font-medium ml-1 flex items-center gap-1">
                        {errors.email.message}
                    </p>
                )}
            </div>

            <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
                    {t('login.password')}
                </label>
                <div className="relative group">
                    <Lock
                        className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors ${errors.password ? "text-red-400" : "text-gray-400 group-focus-within:text-clip-500"
                            }`}
                        size={18}
                    />
                    <input
                        type="password"
                        {...register("password")}
                        className={`w-full pl-11 pr-4 py-3.5 bg-gray-50 border rounded-xl outline-none transition-all font-medium text-gray-900 placeholder:text-gray-400
                            ${errors.password
                                ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                                : "border-gray-200 focus:border-clip-500 focus:ring-2 focus:ring-clip-200"
                            }
                        `}
                        placeholder="••••••••"
                    />
                </div>
                {errors.password && (
                    <p className="text-red-500 text-xs font-medium ml-1 flex items-center gap-1">
                        {errors.password.message}
                    </p>
                )}
            </div>

            <div className="flex justify-end">
                <button
                    type="button"
                    className="text-xs font-bold text-clip-600 hover:text-clip-800 transition-colors"
                    onClick={onForgotPassword}
                >
                    {t('login.forgot_password')}
                </button>
            </div>

            <button
                type="submit"
                disabled={loading}
                className={`w-full py-4 text-white font-bold rounded-xl shadow-lg transition-all active:scale-[0.98] flex items-center justify-center gap-2 mt-2
          ${loading
                        ? "bg-gray-300 cursor-not-allowed shadow-none"
                        : "bg-gray-900 hover:bg-black shadow-gray-200/50 hover:shadow-gray-300/50"
                    }
        `}
            >
                {loading ? (
                    <Loader2 size={20} className="animate-spin" />
                ) : (
                    <>
                        {t('login.signin_btn')}
                        <ArrowRight size={18} />
                    </>
                )}
            </button>
        </form>
    );
};

export default AuthLogin;